import { Box, Card, Stack, Typography } from "@mui/material";
import type { PremiseConfig } from "@shared/types/foyer";
import { useCan } from "../auth/AuthContext";
import { PageContainer, PageHeader } from "../components/PageHeader";
import DemoPaymentsBanner from "../components/offers/DemoPaymentsBanner";
import GuestRequestQueue from "../components/offers/GuestRequestQueue";
import { tokens } from "../theme";

/**
 * Requests — the staff inbox (`POS-9`).
 *
 * ## Why this is its own section and not a panel on Offers
 *
 * Authoring a catalogue and working a queue are two jobs done by two people at
 * two times of day. `canEditLayout` writes the menu; `canHandleGuestRequests`
 * takes the money — the permissions were split by POS-6 for exactly that
 * reason, and a reception desk that has to scroll past a spa package editor to
 * find the order it is meant to deliver is a desk that stops looking. So it
 * hangs beside Offers in `FRONTLAYER_SECTIONS`, at `/frontlayer/requests`,
 * carrying its own permission — which is what makes the section DISAPPEAR for a
 * user who does not hold it rather than open onto a refusal.
 *
 * ## Two layers, one page
 *
 * With a location scoped it is that hotel's queue. With a customer scoped and
 * NO location it is every one of its hotels, stacked and named — the chain
 * shape `CHAIN_CAPABLE_SECTIONS` exists for, and the reason `"requests"` joins
 * `"offers"` in that set. A duty manager covering four properties should not
 * have to change the header scope four times to find out whether anybody is
 * waiting.
 *
 * Care premises are excluded at both layers: offers are hotel-only for v1
 * (§10), and care already has `CareTask` and `ServiceRequestQueue`, which are
 * PHI-shaped and must never be merged with a commercial queue.
 */

interface Props {
  /** Exactly one of the two. A location, or the whole customer. */
  premise?: PremiseConfig;
  companyName?: string;
  /** The customer's hotels, when there is no location scoped. */
  chainPremises?: { premiseId: string; premiseName: string }[];
}

export default function RequestsPage({
  premise,
  companyName,
  chainPremises,
}: Props) {
  // `canEditLayout` is the grandfather clause the route and the nav both
  // carry: nothing backfills a brand-new permission key, so gating on it alone
  // shows every existing operator "not your queue" on the day it ships. The
  // three gates are deleted together once the key has been handed out.
  // Both hooks are called, always: `a() || b()` would skip the second on a
  // render where the first is true, and a hook that runs conditionally is the
  // rule React breaks loudest about.
  const holdsQueueKey = useCan("canHandleGuestRequests");
  const holdsLayoutKey = useCan("canEditLayout");
  const canHandle = holdsQueueKey || holdsLayoutKey;

  if (!canHandle) return <NotYourQueue />;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Promotions"
        title="Requests"
        subtitle={
          premise
            ? "What guests have asked for from the television in their room. Accepting is what books the money."
            : `Every queue ${companyName ?? "this customer"} runs, newest first.`
        }
      />

      {/*
       * §6 requires the "Demo payments" banner on every surface where the
       * always-accepts provider is active, and the request queue is one of
       * them — this is the screen where somebody presses Accept and books a
       * charge, so it is the last place the demo provider should be invisible.
       * The component is POS-8's and asks the server itself; there is
       * deliberately no second banner.
       */}
      <DemoPaymentsBanner />

      {premise ? (
        <GuestRequestQueue
          premiseId={premise.premiseId}
          rooms={premise.rooms}
          defaultLocale={premise.defaultLocale}
        />
      ) : (
        <ChainQueues premises={chainPremises ?? []} />
      )}
    </PageContainer>
  );
}

/**
 * Every hotel of the customer, each with its own queue and its own name above
 * it.
 *
 * One list per premise rather than one merged list with a hotel column: the
 * verbs are premise-scoped on the server, the waiting counts mean nothing added
 * together, and a duty manager reads "Hasselbacken has three waiting" far
 * faster than they scan a mixed list for the rows that are theirs.
 */
function ChainQueues({
  premises,
}: {
  premises: { premiseId: string; premiseName: string }[];
}) {
  if (premises.length === 0) {
    return (
      <Card variant="outlined" sx={{ p: 4 }}>
        <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
          This customer has no hotels, so there is nothing for a guest to ask
          for. Offers and requests are hotel-only.
        </Typography>
      </Card>
    );
  }
  return (
    <Stack spacing={4}>
      {premises.map((p) => (
        <Box key={p.premiseId}>
          <GuestRequestQueue premiseId={p.premiseId} title={p.premiseName} />
        </Box>
      ))}
    </Stack>
  );
}

/**
 * A direct link to a queue somebody may not work.
 *
 * The nav already hides the section, so this is only ever reached by a
 * bookmark or a pasted URL — and it says which right is missing rather than
 * showing an empty table, because an empty queue and a forbidden one look
 * identical and only one of them is worth telling somebody about.
 */
function NotYourQueue() {
  return (
    <PageContainer>
      <PageHeader eyebrow="Promotions" title="Requests" />
      <Card variant="outlined" sx={{ p: 4 }}>
        <Stack spacing={1} alignItems="flex-start">
          <Typography sx={{ fontWeight: 700, color: tokens.ink }}>
            You cannot work this queue
          </Typography>
          <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
            Handling guest requests needs the{" "}
            <strong>Handle guest requests</strong> permission. Ask an
            administrator to add it to your role.
          </Typography>
        </Stack>
      </Card>
    </PageContainer>
  );
}
